import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getReservationReview,
  submitReservationReview,
} from "../../services/welletService";
import LoadingScreen from "../LoadingScreen/LoadingScreen";
import ContentApp from "../ContentApp/ContentApp";
import ErrorPage from "../../screens/Error/ErrorPage";
import Button from "../Common/Button/Button";
import SuccessCheckIcon from "../Icons/SuccessCheckIcon";
import styles from "./reservationReview.module.css";

const Star = ({ filled, onClick, onMouseEnter, onMouseLeave }) => (
  <button
    type="button"
    className={`${styles.star} ${filled ? styles.starActive : ""}`}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    aria-label="star"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="36"
      height="36"
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  </button>
);

const ReservationReview = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [reservation, setReservation] = useState(null);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [stars, setStars] = useState(0);
  const [hoverStars, setHoverStars] = useState(0);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const fetchReview = async () => {
    try {
      const result = await getReservationReview(token);
      setReservation(result);
      if (result.alreadyReviewed) {
        setSubmitted(true);
        setStars(result.existingStars || 0);
        setComment(result.existingComment || "");
      }
    } catch (e) {
      const code = e.response?.data?.errorCode;
      setError(code || "UNKNOWN_ERROR");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReview();
  }, []);

  const handleSubmit = async () => {
    if (stars < 1 || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await submitReservationReview(token, stars, comment);
      setSubmitted(true);
      setReservation(result);
    } catch (e) {
      const code = e.response?.data?.errorCode;
      if (code === "REVIEW_ALREADY_EXISTS") {
        setSubmitted(true);
      } else if (code === "TOKEN_EXPIRED") {
        setSubmitError(t("review.errorTokenExpired"));
      } else {
        setSubmitError(t("review.errorGeneric"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingScreen />;

  if (error) {
    return (
      <ContentApp>
        <ErrorPage />
      </ContentApp>
    );
  }

  if (submitted) {
    return (
      <ContentApp>
        <div
          className={`d-flex align-items-center justify-content-center ${styles.iconContainer}`}
        >
          <SuccessCheckIcon color="var(--color-primary)" size="111px" />
        </div>
        <div className="mt-4 pt-2 px-3">
          <div className={styles.bigTitle}>{t("review.thanks")}</div>
          <div className="text-secondary fw-semibold font-normal text-center mt-3">
            {t("review.thanksSubtitle")}
          </div>
          {reservation?.existingStars > 0 && (
            <div className={`${styles.starsRow}`} style={{ marginTop: "1.5rem" }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} filled={i <= reservation.existingStars} />
              ))}
            </div>
          )}
        </div>
      </ContentApp>
    );
  }

  return (
    <ContentApp>
      {reservation?.showLogoUrl && (
        <div className={styles.headerLogoContainer}>
          <img
            src={reservation.showLogoUrl}
            alt={reservation.showName}
            className={styles.headerLogo}
          />
        </div>
      )}
      {reservation?.showName && (
        <div className={styles.eyebrowName}>{reservation.showName}</div>
      )}
      <div className="px-3 pt-3">
        <div className={styles.bigTitle}>{t("review.title")}</div>
        <div className="text-secondary fw-semibold font-normal text-center mb-2">
          {t("review.subtitle")}
        </div>

        <div className={styles.starsRow}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Star
              key={i}
              filled={i <= (hoverStars || stars)}
              onClick={() => setStars(i)}
              onMouseEnter={() => setHoverStars(i)}
              onMouseLeave={() => setHoverStars(0)}
            />
          ))}
        </div>

        <label className={styles.label} htmlFor="review-comment">
          {t("review.commentLabel")}
        </label>
        <textarea
          id="review-comment"
          className={styles.commentBox}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          maxLength={2000}
          placeholder={t("review.commentPlaceholder")}
        />

        {submitError && (
          <div className="text-danger mt-2 text-center">{submitError}</div>
        )}

        <div className={styles.submitWrapper}>
          <Button
            text={t("review.submit")}
            size="large"
            type="button"
            onClick={handleSubmit}
            disabled={stars < 1 || submitting}
          />
        </div>
      </div>
    </ContentApp>
  );
};

export default ReservationReview;
